import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { ImportDataErrorsModal } from "@/pages/settings/import-data/widgets/ImportDataErrorsModal.tsx";
import { useImportDataAll, useImportDataDelete } from "@/queries/import-data.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiImportData, ApiImportDataStatus } from "@/types/import-data.types.ts";
import {
  Button,
  ButtonLink,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  Page,
  Stack,
  Table,
  Tag,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export function ImportDataPage() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { data, isLoading, isFetching } = useImportDataAll();
  const deleteMutation = useImportDataDelete();
  const { renderDate, renderEmployeeName } = useFormatter();

  useTitle(t("core:settings_import_data"), t("common:settings"));

  const getStatusTag = (status: ApiImportDataStatus) => {
    const statusConfig: Record<
      ApiImportDataStatus,
      { variant: "secondary" | "positive" | "warning" | "danger"; label: string }
    > = {
      [ApiImportDataStatus.draft]: { variant: "secondary", label: t("common:draft") },
      [ApiImportDataStatus.waiting]: { variant: "warning", label: t("common:waiting") },
      [ApiImportDataStatus.in_progress]: { variant: "warning", label: t("common:in_progress") },
      [ApiImportDataStatus.complete]: { variant: "positive", label: t("common:status_completed") },
      [ApiImportDataStatus.fail]: { variant: "danger", label: t("common:status_failed") },
    };

    const config = statusConfig[status];
    return (
      <Tag variant={config.variant} appearance="filled">
        {config.label}
      </Tag>
    );
  };

  const columnHelper = createColumnHelper<ApiImportData>();

  const columns = useMemo<ColumnDef<ApiImportData>[]>(() => {
    return [
      {
        header: t("common:created"),
        cell: (row) => (
          <Text className="text-secondary">{renderDate(row.row.original.datetime_created)}</Text>
        ),
        accessorKey: "datetime_created",
        ...ColumnSizes.date,
      },
      {
        header: t("core:import_data_file_name"),
        cell: (row) => (
          <Text className="text-secondary text-title">{row.row.original.file?.name || "-"}</Text>
        ),
        accessorKey: "file",
        ...ColumnSizes.wide,
      },
      {
        header: t("common:status"),
        cell: (row) => (
          <Stack direction={"horizontal"}>{getStatusTag(row.row.original.status)}</Stack>
        ),
        accessorKey: "status",
        ...ColumnSizes.status,
      },
      {
        header: t("import_data:rows_imported"),
        cell: (row) => (
          <Stack>
            <Text>
              {row.row.original.status === ApiImportDataStatus.in_progress
                ? "-"
                : t("common:of", {
                    current: row.row.original.rows_success,
                    total: row.row.original.rows_total,
                  })}
            </Text>
            {row.row.original.rows_failed > 0 ? (
              <Text
                className="text-danger underline cursor-pointer"
                onClick={() => openModal(ImportDataErrorsModal, { importId: row.row.original.id })}
              >
                {t("plurals:errors", { count: row.row.original.rows_failed })}
              </Text>
            ) : null}
          </Stack>
        ),
        accessorKey: "rows",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:author"),
        cell: (row) => (
          <Link to={`/employees/${row.row.original.author.id}/`} target={"_blank"}>
            <Stack direction={"horizontal"} gap={"sm"} items={"center"} className={"pr-4"}>
              <EmployeeAvatar employee={row.row.original.author} size={"sm"} />
              <Text className={"text-nowrap text-ellipsis text-dark"}>
                {renderEmployeeName(row.row.original.author)}
              </Text>
            </Stack>
          </Link>
        ),
        accessorKey: "author",
        ...ColumnSizes.standard,
      },
      columnHelper.display({
        id: "actions",
        cell: (cell) => {
          const item = cell.row.original;

          return (
            <Stack className="justify-end" gap="sm" direction="horizontal">
              <DropdownMenu
                trigger={
                  <Button variant="secondary">
                    <FontAwesomeIcon icon="fa-light fa-ellipsis" />
                  </Button>
                }
              >
                <DropdownMenuItem
                  variant={"menuDanger"}
                  title={`${t("common:delete")}...`}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                  onClick={() => handleDelete(item)}
                />
              </DropdownMenu>
            </Stack>
          );
        },
        ...ColumnSizes.tiny,
      }),
    ];
  }, [t, renderDate]);

  const handleDelete = useCallback(
    (importData: ApiImportData) => {
      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          await deleteMutation.mutateAsync(importData.id);
        },
      });
    },
    [openModal, t, deleteMutation]
  );

  return (
    <Page>
      <Page.Header showBack={true} title={t("core:settings_import_data")} navigateTo="/settings">
        <ButtonLink
          href="/settings/import-data/new"
          leftIcon={<FontAwesomeIcon icon="fa-light fa-file-import" />}
        >
          {t("core:import_data_start_new")}
        </ButtonLink>
      </Page.Header>
      <Page.Content>
        <Stack direction="vertical" className="px-16">
          <Title header={t("core:import_data_history")} paddingTop paddingBottom size="lg" />
          <Table
            stickyLastColumn={true}
            data={data || []}
            columns={columns}
            showSkeleton={isLoading}
            isFetching={isFetching}
            stickyHeader
            layout={"auto"}
            emptyStateComponent={
              <EmptyStateBase
                emoji=":book:"
                title={t("import_data:empty_state_title")}
                description={t("import_data:empty_state_description")}
                primaryButton={
                  <ButtonLink
                    href="/settings/import-data/new"
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-file-import" />}
                  >
                    {t("import_data:import_data")}
                  </ButtonLink>
                }
              />
            }
          />
        </Stack>
      </Page.Content>
    </Page>
  );
}
